(function () {
  'use strict';

  if (window.OliStoryShopify) return;

  const SELECTOR = '[data-oli-story]';

  function whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function waitForGsap(callback, tries = 0) {
    if (window.gsap && window.ScrollTrigger) {
      callback();
      return;
    }
    if (tries > 80) {
      console.error('[OliStory] GSAP or ScrollTrigger failed to load.');
      return;
    }
    window.setTimeout(() => waitForGsap(callback, tries + 1), 100);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  class NarrativeController {
    constructor(root, assets) {
      this.root = root;
      this.assets = assets;
      this.spacer = root.querySelector('[data-oli-story-narrative]');
      this.stage = root.querySelector('.oli-story__narrative-stage');
      this.canvas = root.querySelector('.oli-story__narrative-canvas');
      this.ctx = this.canvas.getContext('2d');
      this.heroOverlay = root.querySelector('.oli-story__hero-overlay');
      this.stageIcon = root.querySelector('[data-oli-story-stage-icon]');
      this.stages = root.querySelectorAll('.oli-story__stage');
      this.detailGuide = root.querySelector('[data-oli-story-detail-guide]');
      this.detailSteps = this.detailGuide ? Array.from(this.detailGuide.querySelectorAll('[data-oli-story-detail-step]')) : [];
      this.colorGuide = root.querySelector('[data-oli-story-color-guide]');
      this.colorSwatches = this.colorGuide ? Array.from(this.colorGuide.querySelectorAll('[data-oli-story-color-swatch]')) : [];
      this.loading = root.querySelector('[data-oli-story-loading]');
      this.loadingBar = root.querySelector('[data-oli-story-loading-bar]');
      this.loadingPercent = root.querySelector('[data-oli-story-loading-percent]');
      this.fade = 0.1;
      this.colorGuideFade = 0.12;
      this.stageRanges = [];
      this.images = [];
      this.frameCount = assets.frames.length;
      this.currentFrame = -1;
      this.lastDrawn = -1;
      this.currentProgress = 0;
      this.displayWidth = 0;
      this.displayHeight = 0;
      this.activeDetailIndex = -1;
      this.activeColorIndex = -1;
      this.mobileQuery = window.matchMedia('(max-width: 768px)');
      this.detailRange = this.detailGuide ? {
        start: parseFloat(this.detailGuide.dataset.progressStart) || 0.52,
        end: parseFloat(this.detailGuide.dataset.progressEnd) || 0.76,
      } : null;
      this.detailHoldFrame = this.detailGuide ? parseInt(this.detailGuide.dataset.holdFrame, 10) : NaN;
      this.detailAnnotationStart = 0.16;
      this.colorFrameStops = [150, 190, 220];
      this.colorGuideRange = this.colorGuide ? {
        start: parseFloat(this.colorGuide.dataset.progressStart) || 0.76,
        end: parseFloat(this.colorGuide.dataset.progressEnd) || 1,
      } : null;

      if (this.heroOverlay) {
        this.stageRanges.push({
          el: this.heroOverlay,
          start: parseFloat(this.heroOverlay.dataset.progressStart),
          end: parseFloat(this.heroOverlay.dataset.progressEnd),
          isHero: true,
        });
      }

      Array.from(this.stages).forEach((stage) => {
        this.stageRanges.push({
          el: stage,
          start: parseFloat(stage.dataset.progressStart),
          end: parseFloat(stage.dataset.progressEnd),
          isHero: false,
        });
      });

      this.stageRanges.sort((a, b) => a.start - b.start);
      this.init();
    }

    init() {
      window.gsap.registerPlugin(window.ScrollTrigger);
      this.resizeCanvas();
      window.addEventListener('resize', () => this.resizeCanvas(), { passive: true });

      this.preloadFrames()
        .then(() => {
          if (this.loading) this.loading.classList.add('is-hidden');
          this.lastDrawn = -1;
          this.drawFrame(0);
          this.bindScroll();
        })
        .catch((error) => {
          console.error('[OliStory] Frame preload failed:', error);
          this.updateStages(0);
        });
    }

    resizeCanvas() {
      const dpr = window.devicePixelRatio || 1;
      const rect = this.stage.getBoundingClientRect();
      const width = rect.width || window.innerWidth;
      const height = rect.height || window.innerHeight;

      this.canvas.width = width * dpr;
      this.canvas.height = height * dpr;
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.scale(dpr, dpr);
      this.displayWidth = width;
      this.displayHeight = height;

      if (this.currentFrame >= 0) this.drawFrame(this.currentFrame, true);
    }

    preloadFrames() {
      let loaded = 0;
      const promises = this.assets.frames.map((src, index) => new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          loaded += 1;
          this.updateLoadingProgress(loaded);
          resolve(true);
        };
        image.onerror = () => {
          loaded += 1;
          this.updateLoadingProgress(loaded);
          resolve(false);
        };
        image.src = src;
        this.images[index] = image;
      }));

      return Promise.all(promises);
    }

    updateLoadingProgress(loaded) {
      const percent = clamp(Math.round((loaded / this.frameCount) * 100), 0, 100);
      if (this.loadingBar) this.loadingBar.style.width = percent + '%';
      if (this.loadingPercent) this.loadingPercent.textContent = percent + '%';
    }

    drawFrame(index, force) {
      if (!force && this.lastDrawn === index && this.lastDrawn !== -1) return;
      const image = this.images[index];
      if (!image || !image.complete || image.naturalWidth === 0) return;

      this.lastDrawn = index;
      this.currentFrame = index;

      const width = this.displayWidth;
      const height = this.displayHeight;
      const isMobile = this.mobileQuery.matches;
      const ratio = isMobile
        ? Math.min(width / image.naturalWidth, height / image.naturalHeight) * this.mobileFrameScale(this.currentProgress)
        : Math.max(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * ratio;
      const drawHeight = image.naturalHeight * ratio;
      const drawX = ((width - drawWidth) / 2) + (isMobile ? this.mobileFrameOffsetX(this.currentProgress) : 0);
      const drawY = (height - drawHeight) / 2;

      this.ctx.clearRect(0, 0, width, height);
      this.ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    }

    mobileFrameScale(progress) {
      const points = [
        { at: 0, scale: 1 },
        { at: 0.08, scale: 1.1 },
        { at: 0.22, scale: 1.42 },
        { at: 0.36, scale: 1.58 },
        { at: 0.52, scale: 1.5 },
        { at: 0.64, scale: 1.72 },
        { at: 0.76, scale: 1.66 },
        { at: 0.88, scale: 1.5 },
        { at: 1, scale: 1.34 },
      ];

      for (let index = 0; index < points.length - 1; index += 1) {
        const current = points[index];
        const next = points[index + 1];
        if (progress >= current.at && progress <= next.at) {
          const local = clamp((progress - current.at) / (next.at - current.at), 0, 1);
          const eased = local * local * (3 - 2 * local);
          return current.scale + (next.scale - current.scale) * eased;
        }
      }

      return points[points.length - 1].scale;
    }

    mobileFrameOffsetX(progress) {
      return progress > 0.08 ? -70 : 0;
    }

    bindScroll() {
      const whiteHoldFrames = 6;
      const whiteHoldProgress = whiteHoldFrames / this.frameCount;
      const frameForProgress = (progress) => {
        if (progress < whiteHoldProgress) return 0;

        const effectiveProgress = (progress - whiteHoldProgress) / (1 - whiteHoldProgress);
        return clamp(Math.floor(effectiveProgress * (this.frameCount - 1)), 0, this.frameCount - 1);
      };
      const smooth = (value) => value * value * (3 - 2 * value);
      const configuredDetailHoldFrame = Number.isFinite(this.detailHoldFrame)
        ? clamp(this.detailHoldFrame - 1, 0, this.frameCount - 1)
        : null;
      const detailEntryFrame = this.detailRange ? frameForProgress(this.detailRange.start) : null;
      const detailHoldFrame = configuredDetailHoldFrame !== null ? configuredDetailHoldFrame : detailEntryFrame;
      const frameForNarrative = (progress) => {
        if (!this.detailRange || detailEntryFrame === null || detailHoldFrame === null) {
          return frameForProgress(progress);
        }

        const { start, end } = this.detailRange;
        const duration = end - start;
        if (duration <= 0) return frameForProgress(progress);

        if (progress >= start && progress <= end) {
          const normalized = clamp((progress - start) / duration, 0, 1);
          const ramp = this.detailAnnotationStart;

          if (normalized < ramp) {
            const intro = smooth(normalized / ramp);
            return Math.round(detailEntryFrame + (detailHoldFrame - detailEntryFrame) * intro);
          }

          return detailHoldFrame;
        }

        if (progress > end) {
          const postProgress = clamp((progress - end) / (1 - end), 0, 1);
          return Math.round(detailHoldFrame + (this.frameCount - 1 - detailHoldFrame) * postProgress);
        }

        return frameForProgress(progress);
      };

      const onUpdate = (self) => {
        const progress = self.progress;
        this.currentProgress = progress;
        const frameIndex = frameForNarrative(progress);

        if (frameIndex !== this.currentFrame) this.drawFrame(frameIndex);
        this.updateStages(progress);
        this.updateStageIcon(progress);
        this.updateDetailGuide(progress);
        this.updateColorGuide(progress);
        this.stage.classList.toggle('is-complete', progress >= 1);
      };

      this.scrollTrigger = window.ScrollTrigger.create({
        trigger: this.spacer,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.3,
        anticipatePin: 0,
        onUpdate,
      });

      onUpdate(this.scrollTrigger);
    }

    updateStages(progress) {
      this.stageRanges.forEach((range) => {
        range.el.classList.remove('is-active');
        range.el.style.opacity = '0';
        if (range.isHero) {
          range.el.style.transform = 'translate(-50%, -50%) scale(1)';
        } else {
          range.el.style.transform = 'translate3d(0, 34px, 0)';
        }
      });

      this.stageRanges.forEach((range) => {
        const { start, end, isHero } = range;
        let opacity = 0;

        let inRange = 0;

        if (progress >= start && progress <= end) {
          inRange = (progress - start) / (end - start);
          const holdsToEnd = end >= 1;

          if (isHero) {
            const heroFade = 0.5;
            opacity = inRange > 1 - heroFade ? (1 - inRange) / heroFade : 1;
          } else if (inRange < this.fade) {
            opacity = inRange / this.fade;
          } else if (!holdsToEnd && inRange > 1 - this.fade) {
            opacity = (1 - inRange) / this.fade;
          } else {
            opacity = 1;
          }
        }

        if (opacity > 0) {
          range.el.classList.add('is-active');
          range.el.style.opacity = opacity.toFixed(3);

          if (isHero) {
            const exitProgress = 1 - opacity;
            const lift = (exitProgress * 8).toFixed(2);
            const scale = (1 - exitProgress * 0.08).toFixed(3);
            range.el.style.transform = `translate(-50%, calc(-50% - ${lift}vh)) scale(${scale})`;
          } else {
            let lift = 0;

            if (inRange < this.fade) {
              const enter = clamp(inRange / this.fade, 0, 1);
              lift = 34 * (1 - enter);
            } else if (end < 1 && inRange > 1 - this.fade) {
              const exit = clamp((inRange - (1 - this.fade)) / this.fade, 0, 1);
              lift = -34 * exit;
            }

            range.el.style.transform = `translate3d(0, ${lift.toFixed(2)}px, 0)`;
          }
        }
      });
    }

    updateStageIcon(progress) {
      if (!this.stageIcon) return;

      const start = 0.08;
      const end = 1;
      const fade = 0.08;
      let opacity = 0;

      if (progress >= start && progress <= end) {
        const normalized = clamp((progress - start) / (end - start), 0, 1);

        if (normalized < fade) {
          opacity = normalized / fade;
        } else if (end < 1 && normalized > 1 - fade) {
          opacity = (1 - normalized) / fade;
        } else {
          opacity = 1;
        }
      }

      this.stageIcon.classList.toggle('is-visible', opacity > 0);
      this.stageIcon.style.opacity = opacity.toFixed(3);
    }

    updateDetailGuide(progress) {
      if (!this.detailGuide || this.detailSteps.length === 0 || !this.detailRange) return;

      const { start, end } = this.detailRange;
      const duration = end - start;
      if (duration <= 0) return;

      let opacity = 0;
      let normalized = 0;

      if (progress >= start && progress <= end) {
        normalized = clamp((progress - start) / duration, 0, 1);
        const fade = 0.08;
        const intro = this.detailAnnotationStart;

        if (normalized < intro) {
          opacity = clamp(normalized / intro, 0, 1);
        } else if (normalized > 1 - fade) {
          opacity = (1 - normalized) / fade;
        } else {
          opacity = 1;
        }
      }

      const isVisible = opacity > 0;
      const stepProgress = clamp(
        (normalized - this.detailAnnotationStart) / (1 - this.detailAnnotationStart),
        0,
        1
      );
      const activeIndex = isVisible
        ? clamp(Math.floor(stepProgress * this.detailSteps.length), 0, this.detailSteps.length - 1)
        : -1;

      this.detailGuide.classList.toggle('is-visible', isVisible);
      this.detailGuide.style.opacity = opacity.toFixed(3);
      this.stage.classList.toggle('is-detail-guide-active', isVisible);
      this.root.classList.toggle('is-detail-guide-active', isVisible);

      if (activeIndex === this.activeDetailIndex) return;
      this.activeDetailIndex = activeIndex;

      this.detailSteps.forEach((step, index) => {
        const isActive = index === activeIndex;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    updateColorGuide(progress) {
      if (!this.colorGuide || this.colorSwatches.length === 0 || !this.colorGuideRange) return;

      const { start, end } = this.colorGuideRange;
      const duration = end - start;
      if (duration <= 0) return;

      let opacity = 0;
      let normalized = 0;

      if (progress >= start && progress <= end) {
        normalized = clamp((progress - start) / duration, 0, 1);

        if (normalized < this.colorGuideFade) {
          opacity = normalized / this.colorGuideFade;
        } else if (end < 1 && normalized > 1 - this.colorGuideFade) {
          opacity = (1 - normalized) / this.colorGuideFade;
        } else {
          opacity = 1;
        }
      }

      const activeIndex = opacity > 0
        ? this.colorIndexForProgress(normalized)
        : -1;

      this.colorGuide.classList.toggle('is-visible', opacity > 0);
      this.colorGuide.style.opacity = opacity.toFixed(3);

      if (activeIndex === this.activeColorIndex) return;
      this.activeColorIndex = activeIndex;

      this.colorSwatches.forEach((swatch, index) => {
        const isActive = index === activeIndex;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    colorIndexForProgress(normalized) {
      const holdFrame = Number.isFinite(this.detailHoldFrame)
        ? clamp(this.detailHoldFrame - 1, 0, this.frameCount - 1)
        : 129;
      const lastFrame = this.frameCount - 1;
      const span = Math.max(1, lastFrame - holdFrame);
      const stops = this.colorFrameStops.map((frame) => clamp((frame - 1 - holdFrame) / span, 0, 1));

      if (normalized < stops[0]) return -1;
      if (normalized < stops[1]) return 0;
      if (normalized < stops[2]) return 1;
      return 2;
    }
  }

  class ParallaxController {
    constructor(section) {
      this.section = section;
      this.init();
    }

    init() {
      const bgImg = this.section.querySelector('.oli-story__bg-layer img');
      if (bgImg) {
        window.gsap.fromTo(bgImg,
          { y: 0, scale: 1.08 },
          {
            y: -60,
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: this.section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.5,
            },
          }
        );
      }

      const productCopy = this.section.querySelector('.oli-story__product-copy');
      if (productCopy) {
        window.gsap.from(productCopy, {
          opacity: 0,
          y: 28,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: this.section,
            start: 'top 68%',
            once: true,
          },
        });
      }

      const caption = this.section.querySelector('.oli-story__scene-caption, .oli-story__product-cta');
      if (caption) {
        window.gsap.from(caption, {
          opacity: 0,
          y: 30,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: this.section,
            start: 'top 70%',
            once: true,
          },
        });
      }
    }
  }

  class SceneStackController {
    constructor(stack) {
      this.stack = stack;
      this.panels = Array.from(stack.querySelectorAll('[data-oli-story-scene-panel]'));
      this.dots = Array.from(stack.querySelectorAll('[data-oli-story-scene-dot]'));
      this.timers = [];
      this.isSnapping = false;
      this.activeIndex = 0;
      this.autoTimer = null;
      this.touchStart = null;
      this.mobileQuery = window.matchMedia('(max-width: 768px)');
      this.init();
    }

    init() {
      this.bindMobileCarousel();
      this.applyMode();
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', () => this.applyMode());
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(() => this.applyMode());
      }

      window.ScrollTrigger.create({
        trigger: this.stack,
        start: 'top 98%',
        end: 'bottom 2%',
        onEnter: () => this.mobileQuery.matches ? this.enterMobile() : this.enter(),
        onEnterBack: () => this.mobileQuery.matches ? this.enterMobile() : this.enter(),
        onLeave: () => this.mobileQuery.matches ? this.leaveMobile() : this.leave(),
        onLeaveBack: () => this.mobileQuery.matches ? this.leaveMobile() : this.leave(),
      });
    }

    enter() {
      this.clearTimers();
      this.snapToStack();
      this.panels.forEach((panel, index) => {
        this.timers.push(window.setTimeout(() => {
          panel.classList.add('is-entered');
          this.timers.push(window.setTimeout(() => {
            panel.classList.add('is-copy-visible');
          }, 180));
        }, index * 160));
      });
    }

    leave() {
      this.clearTimers();
      this.panels.forEach((panel) => {
        panel.classList.remove('is-copy-visible', 'is-entered');
      });
    }

    clearTimers() {
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers = [];
    }

    snapToStack() {
      if (this.isSnapping || snapToElement(this.stack) === false) return;
      this.isSnapping = true;
      window.setTimeout(() => {
        this.isSnapping = false;
      }, 650);
    }

    bindMobileCarousel() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.setMobileActive(index);
          this.restartMobileAuto();
        });
      });

      this.stack.addEventListener('touchstart', (event) => {
        if (!this.mobileQuery.matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });

      this.stack.addEventListener('touchend', (event) => {
        if (!this.mobileQuery.matches || !this.touchStart || event.changedTouches.length !== 1) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStart.x;
        const dy = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        this.setMobileActive(this.activeIndex + (dx < 0 ? 1 : -1));
        this.restartMobileAuto();
      }, { passive: true });
    }

    applyMode() {
      if (this.mobileQuery.matches) {
        this.setMobileActive(this.activeIndex);
        return;
      }

      this.stopMobileAuto();
      this.panels.forEach((panel) => {
        panel.classList.remove('is-mobile-active', 'is-mobile-prev');
      });
      this.dots.forEach((dot) => {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      });
    }

    enterMobile() {
      this.snapToStack();
      this.clearTimers();
      this.setMobileActive(this.activeIndex);
      this.startMobileAuto();
    }

    leaveMobile() {
      this.stopMobileAuto();
    }

    setMobileActive(index) {
      if (this.panels.length === 0) return;
      const next = (index + this.panels.length) % this.panels.length;
      this.activeIndex = next;

      this.panels.forEach((panel, panelIndex) => {
        panel.classList.add('is-entered');
        panel.classList.toggle('is-copy-visible', panelIndex === next);
        panel.classList.toggle('is-mobile-active', panelIndex === next);
        panel.classList.toggle('is-mobile-prev', panelIndex < next);
        panel.setAttribute('aria-hidden', panelIndex === next ? 'false' : 'true');
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === next;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    startMobileAuto() {
      if (this.autoTimer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.autoTimer = window.setInterval(() => {
        this.setMobileActive(this.activeIndex + 1);
      }, 4200);
    }

    stopMobileAuto() {
      if (!this.autoTimer) return;
      window.clearInterval(this.autoTimer);
      this.autoTimer = null;
    }

    restartMobileAuto() {
      this.stopMobileAuto();
      this.startMobileAuto();
    }
  }

  class SnapSectionController {
    constructor(section) {
      this.section = section;
      this.isSnapping = false;
      this.init();
    }

    init() {
      window.ScrollTrigger.create({
        trigger: this.section,
        start: 'top 90%',
        end: 'bottom 10%',
        onEnter: () => this.snap(),
        onEnterBack: () => this.snap(),
      });
    }

    snap() {
      if (this.isSnapping || snapToElement(this.section) === false) return;
      this.isSnapping = true;
      window.setTimeout(() => {
        this.isSnapping = false;
      }, 650);
    }
  }

  function snapToElement(element) {
    const targetTop = element.getBoundingClientRect().top + window.scrollY;
    if (Math.abs(window.scrollY - targetTop) < 2) return false;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
    return true;
  }

  function readAssets(root) {
    const node = root.querySelector('[data-oli-story-assets]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[OliStory] Invalid asset manifest:', error);
      return null;
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.oliStoryReady === 'true') return;
      const assets = readAssets(root);
      if (!assets || !Array.isArray(assets.frames) || assets.frames.length === 0) return;

      root.dataset.oliStoryReady = 'true';
      root.classList.add('is-story-ready');
      document.body.classList.add('oli-story-page');
      new NarrativeController(root, assets);
      root.querySelectorAll('.oli-story__scene-stack').forEach((stack) => {
        new SceneStackController(stack);
      });
      root.querySelectorAll('.oli-story__section-product').forEach((section) => {
        new SnapSectionController(section);
        new ParallaxController(section);
      });
      requestAnimationFrame(() => window.ScrollTrigger.refresh());
    });
  }

  window.OliStoryShopify = { init };

  whenReady(() => waitForGsap(init));
})();
